import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player/lazy";
import Api from "@/api/Api";
import Button from "@/components/button/Button";
import PlayButton from "@/components/button/PlayButton";
import Input from "@/components/form/Input";
import Label from "@/components/form/Label";
import ThemeRail from "@/components/theme/ThemeRail";
import MemoryComments from "@/contexts/memories/components/MemoryComments";
import { useOnLoadImages } from "@/helpers/hooks/useOnLoadImages";
import { flashMessageFromResponseError } from "@/utils/alert_utils";
import { replaceItemInArray } from "@/utils/array_utils";
import Modeler from "@/utils/modeler/modeler";
import { fullName } from "@/utils/project_utils";
import { route } from "@/utils/route_utils";
import { addHyperlink, sanitizeUserInput } from "@/utils/string_utils";
import { useLocation } from "react-router-dom";
const MemoryShow = (props) => {
  const {
    currentUser,
    isAdmin,
    memory,
    onBlurInput,
    onFocusInput,
    onUpdateMemory,
    project,
    modeledProjectUser,
    setModalNotificationSettingVisible,
    setModeledProjectUser
  } = props;
  const componentInputRef = useRef(null);
  const initialFormState = {
    display_name: "",
    text: ""
  };
  const [comments, setComments] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [isVideoPlayerError, setIsVideoPlayerError] = useState(false);
  const { t } = useTranslation();
  const getSectionQuery = (searchString) => {
    const queryParams = new URLSearchParams(searchString);
    return queryParams.get("section");
  };
  const { search } = useLocation();
  const imagesWrapperRef = useRef(null);
  const shouldDisplayDescription = (memory.memory_images.length !== 0 || memory.memory_video) && memory.description;
  const openConfirmationModal = (comment) => {
    setIsConfirmationModalVisible(true);
    setSelectedComment(comment);
  };
  const closeConfirmationModal = () => {
    setIsConfirmationModalVisible(false);
  };
  const imagesLoaded = useOnLoadImages(imagesWrapperRef);
  useEffect(() => {
    if (!memory)
      return;
    mixpanel.track("memory_viewed", {
      kind: "project",
      memory_id: memory.id,
      project_id: project.id
    });
  }, [memory]);
  useEffect(() => {
    setIsVideoPlayerError(false);
    setComments(memory.comments);
  }, [memory]);
  useEffect(() => {
    const sectionQuery = getSectionQuery(search);
    if (sectionQuery === "comment" && imagesLoaded) {
      componentInputRef.current.scrollIntoView({
        block: "end",
        behavior: "smooth"
      });
    }
  }, [imagesLoaded]);
  const activeCommentsLength = useMemo(() => {
    return comments.filter((comment) => comment.status === "posted").length;
  }, [comments]);
  const formatBody = () => {
    return {
      comment: {
        display_name: form.display_name,
        memory_id: memory.id,
        text: form.text,
        user_id: currentUser ? currentUser.id : null
      }
    };
  };
  const handleCreateComment = async () => {
    if (modeledProjectUser && modeledProjectUser.status === "restricted") {
      window.location.href = route("projectAccessDenied", { slug: project.slug, action: "comment" });
      return;
    }
    setSubmittingComment(true);
    const url = route("comments");
    const body = formatBody();
    try {
      const response = await Api.utility.post(url, body);
      const newComment = new Modeler(response.data.new_comment).build();
      const isNewProjectUser = response.data.is_new_project_user;
      mixpanel.track("comment_created", {
        kind: "project",
        memory_id: memory.id,
        project_id: project.id
      });
      const updatedMemory = {
        ...memory,
        comments: [...memory.comments, newComment]
      };
      onUpdateMemory(memory.id, updatedMemory);
      setComments([...comments, newComment]);
      setSubmittingComment(false);
      if (isNewProjectUser) {
        const newProjectUser = new Modeler(
          response.data.new_project_user
        ).build();
        setModeledProjectUser(newProjectUser);
        setModalNotificationSettingVisible(true);
      }
      setForm({
        ...form,
        text: ""
      });
    } catch (err) {
      setSubmittingComment(false);
      flashMessageFromResponseError(err);
    }
  };
  const handleToggleCommentStatus = async (comment) => {
    const isCommentHidden = comment.status === "hidden";
    const url = route("comment", { id: comment.id });
    const body = {
      status: isCommentHidden ? "posted" : "hidden"
    };
    try {
      const response = await Api.utility.patch(url, body);
      const updatedComment = new Modeler(response.data).build();
      const newComments = replaceItemInArray(comments, updatedComment);
      setComments(newComments);
      const updatedMemory = {
        ...memory,
        comments: newComments
      };
      onUpdateMemory(memory.id, updatedMemory);
    } catch (err) {
      flashMessageFromResponseError(err);
    }
  };
  const handleUpdateComment = async (comment, _) => {
    const url = route("comment", { id: comment.id });
    const body = {
      comment: {
        text: comment.text
      }
    };
    try {
      const response = await Api.utility.patch(url, body);
      const updatedComment = new Modeler(response.data).build();
      const newComments = replaceItemInArray(comments, updatedComment);
      setComments(newComments);
      const updatedMemory = {
        ...memory,
        comments: newComments
      };
      onUpdateMemory(memory.id, updatedMemory);
    } catch (err) {
      flashMessageFromResponseError(err);
    }
  };
  const deleteComment = async (comment) => {
    const url = route("comment", { id: comment.id });
    await Api.utility.delete(url);
    const newComments = comments.filter(
      (existingComment) => existingComment !== comment
    );
    setComments(newComments);
    const updatedMemory = {
      ...memory,
      comments: newComments
    };
    onUpdateMemory(memory.id, updatedMemory);
    setIsConfirmationModalVisible(false);
  };
  const updateField = (field, value) => {
    setForm({
      ...form,
      [field]: value
    });
  };
  const renderImages = () => {
    if (memory.memory_images.length) {
      return memory.memory_images.map((image) => /* @__PURE__ */ React.createElement("div", { key: image.id }, /* @__PURE__ */ React.createElement(
        "img",
        {
          alt: `Memory image of ${fullName(project)}`,
          src: image.image_url,
          className: "memory-display__image"
        }
      )));
    } else if (memory.memory_video && memory.memory_video.video_url) {
      const imageUrl = memory.memory_video.video_thumbnail_url || require("@/assets/images/project_default.jpg");
      return isVideoPlayerError ? /* @__PURE__ */ React.createElement("div", { className: "memory-display__video-wrapper" }, /* @__PURE__ */ React.createElement("a", { href: memory.memory_video.video_url, target: "_blank" }, /* @__PURE__ */ React.createElement("img", { src: imageUrl }), /* @__PURE__ */ React.createElement(PlayButton, null))) : /* @__PURE__ */ React.createElement("div", { className: "memory-display__video-wrapper" }, /* @__PURE__ */ React.createElement(
        ReactPlayer,
        {
          url: memory.memory_video.video_url,
          controls: true,
          onError: () => setIsVideoPlayerError(true)
        }
      ));
    }
  };
  const renderTopDescription = () => {
    if (!memory.memory_images.length && !memory.memory_video && memory.description) {
      return /* @__PURE__ */ React.createElement("div", { className: "memory-display__description-wrapper" }, /* @__PURE__ */ React.createElement("div", { className: "columns is-centered is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column" }, /* @__PURE__ */ React.createElement(
        "div",
        {
          className: "memory-display__description prose max-w-none",
          dangerouslySetInnerHTML: {
            __html: sanitizeUserInput(
              addHyperlink(memory.description),
              memory.user
            )
          }
        }
      ))));
    }
  };
  const renderVideoUrl = () => {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", { className: "memory-display__description-caption" }, t("views.memories.display.clickHere")), /* @__PURE__ */ React.createElement(
      "a",
      {
        className: "memory-display__url",
        href: memory.memory_video.video_url,
        target: "_blank"
      },
      memory.memory_video.video_url
    ));
  };
  const postOnBehalfOfSomeone = () => {
    if (memory.on_behalf_of) {
      return /* @__PURE__ */ React.createElement("p", { className: "memory-display__contributor-label" }, t("form.description.memory.memoryPostOnBehalf", {
        userName: memory.on_behalf_of,
        contributorName: memory.contributed_by
      }));
    }
  };
  const renderBottomDescription = () => {
    if (shouldDisplayDescription) {
      return /* @__PURE__ */ React.createElement("div", { className: "columns is-centered is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column memory-display__description-caption-wrapper" }, memory.memory_video && isVideoPlayerError && renderVideoUrl(), /* @__PURE__ */ React.createElement(
        "div",
        {
          className: "memory-display__description-caption prose max-w-none",
          dangerouslySetInnerHTML: {
            __html: addHyperlink(memory.description)
          }
        }
      )));
    } else if (memory.memory_video && isVideoPlayerError) {
      return /* @__PURE__ */ React.createElement("div", { className: "columns is-centered is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column memory-display__description-caption-wrapper" }, renderVideoUrl()));
    }
  };
  const updateCommentField = (index, value) => {
    const comment = comments[index];
    const updatedComment = {
      ...comment,
      ["text"]: value
    };
    const newArray = [...comments];
    newArray[index] = updatedComment;
    setComments(newArray);
  };
  const redirectPath = route("projectMemory", {
    slug: project.slug,
    memory_id: memory.id,
    section: "comment"
  });
  const loginPath = route("signInPage", {
    redirect_path: redirectPath
  });
  const signupPath = route("signUpPage", {
    redirect_path: redirectPath
  });
  return /* @__PURE__ */ React.createElement("div", { className: "memory-show", ref: imagesWrapperRef }, renderImages(), renderTopDescription(), /* @__PURE__ */ React.createElement("div", { className: "memory-display__wrapper" }, memory.memory_quote && /* @__PURE__ */ React.createElement("div", { className: "columns is-mobile is-centered" }, /* @__PURE__ */ React.createElement("div", { className: "column" }, /* @__PURE__ */ React.createElement("p", { className: "memory-display__quote" }, memory.memory_quote.text))), /* @__PURE__ */ React.createElement("div", { className: "columns is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column memory-display__section-wrapper" }, /* @__PURE__ */ React.createElement("p", { className: "memory-display__contributor-label" }, t("form.label.memory.memoryContributor")), /* @__PURE__ */ React.createElement("p", { className: "memory-display__contributor" }, memory.contributed_by), postOnBehalfOfSomeone())), renderBottomDescription(), memory.relationship_description && /* @__PURE__ */ React.createElement("div", { className: "columns is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column memory-display__section-wrapper with-narrow-padding-top" }, /* @__PURE__ */ React.createElement("p", { className: "memory-display__context-label" }, t("form.label.memory.memoryContext")), /* @__PURE__ */ React.createElement("p", { className: "memory-display__context" }, memory.relationship_description))), /* @__PURE__ */ React.createElement("div", { className: "columns is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column" }, /* @__PURE__ */ React.createElement(ThemeRail, { themes: memory.themes, selectedThemes: memory.themes }))), /* @__PURE__ */ React.createElement("div", { className: "columns is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column memory-display__comments" }, /* @__PURE__ */ React.createElement("h3", null, activeCommentsLength ? t("views.memories.actions.totalComments", {
    total: activeCommentsLength
  }) : t("views.memories.actions.comments")))), !(currentUser == null ? void 0 : currentUser.id) ? /* @__PURE__ */ React.createElement("p", { className: "memory-display__context" }, /* @__PURE__ */ React.createElement("span", null, t("views.memories.actions.loginSignupMessage")), /* @__PURE__ */ React.createElement("a", { className: "devise_form__login_anchor", href: loginPath }, t("views.memories.actions.login")), /* @__PURE__ */ React.createElement("span", null, t("views.memories.actions.loginSignupSeparator")), /* @__PURE__ */ React.createElement("a", { className: "devise_form__login_anchor", href: signupPath }, t("views.memories.actions.signup"))) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "columns is-mobile" }, /* @__PURE__ */ React.createElement("div", { className: "column memory-display__comment-text-label-wrapper" }, /* @__PURE__ */ React.createElement(Label, { labelSize: "small", withoutPadding: true }, t("form.label.memory.memoryComment")))), /* @__PURE__ */ React.createElement("div", { className: "columns is-mobile memory-display__comment-text-field-wrapper" }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "column is-12-mobile is-10-tablet memory-display__comment-text-field",
      ref: componentInputRef
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        onBlur: () => onBlurInput("comment"),
        onChange: (value) => updateField("text", value),
        onFocus: () => onFocusInput("comment"),
        placeholder: t(
          "form.placeholder.memory.memoryComment"
        ),
        value: form.text
      }
    )
  ), /* @__PURE__ */ React.createElement("div", { className: "column is-4-mobile is-2-tablet memory-display__comment-post-button my-2" }, /* @__PURE__ */ React.createElement(Button, { color: "outline", onClick: handleCreateComment, isDisabled: submittingComment }, t("form.actions.post"))))), /* @__PURE__ */ React.createElement(
    MemoryComments,
    {
      closeConfirmationModal,
      comment: selectedComment,
      comments,
      currentUser,
      isAdmin,
      isConfirmationModalVisible,
      onDeleteClick: openConfirmationModal,
      onDeleteConfirm: deleteComment,
      onSaveClick: handleUpdateComment,
      onStatusClick: handleToggleCommentStatus,
      onUpdateComment: updateCommentField
    }
  )));
};
export default MemoryShow;
